import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import ResponseOverview from './components/ResponseOverview';
import QuestionWiseAnalysis from './components/QuestionWiseAnalysis';
import TimelineTracking from './components/TimelineTracking';
import HistoricalComparison from './components/HistoricalComparison';
import SiteComparison from './components/SiteComparison';
import ResponseQualityMetrics from './components/ResponseQualityMetrics';
import IndividualProgressTracker from './components/IndividualProgressTracker';

const AuditResponseTracker = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auditId = searchParams?.get('audit') || 'AUD-001';
  
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedLocation, setSelectedLocation] = useState('all');

  // Mock audit data
  const auditData = {
    id: auditId,
    title: 'Q4 2025 Workplace Safety Audit',
    description: 'Comprehensive workplace safety assessment covering all departments',
    location: 'Plant 1 - Main Facility',
    createdDate: '2025-11-15',
    publishDate: '2025-11-20',
    dueDate: '2025-12-31',
    totalQuestions: 45,
    totalUsers: 45,
    completedUsers: 35,
    responseRate: 78,
    averageScore: 87,
    status: 'active'
  };

  const tabs = [
    { id: 'overview', label: 'Response Overview', icon: 'BarChart3' },
    { id: 'questions', label: 'Question-wise Analysis', icon: 'FileQuestion' },
    { id: 'timeline', label: 'Timeline Tracking', icon: 'Clock' },
    { id: 'historical', label: 'Historical Comparison', icon: 'TrendingUp' },
    { id: 'sites', label: 'Site Comparison', icon: 'MapPin' },
    { id: 'quality', label: 'Response Quality', icon: 'CheckCircle' },
    { id: 'individuals', label: 'Individual Progress', icon: 'Users' }
  ];

  const handleExportReport = () => {
    console.log('Exporting report for audit:', auditId);
  };

  const handleBackToDashboard = () => {
    navigate('/safety-audit-dashboard');
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBackToDashboard}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <Icon name="ArrowLeft" size={20} />
          <span>Back to Dashboard</span>
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-foreground">{auditData?.title}</h1>
              <span className="px-3 py-1 bg-green-500/10 text-green-700 border border-green-200 rounded-full text-sm font-medium">
                <Icon name="Activity" size={12} className="inline mr-1" />
                {auditData?.status?.charAt(0)?.toUpperCase() + auditData?.status?.slice(1)}
              </span>
            </div>
            <p className="text-muted-foreground mb-4">{auditData?.description}</p>
            
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="MapPin" size={14} />
                <span>{auditData?.location}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={14} />
                <span>Due: {auditData?.dueDate}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="FileQuestion" size={14} />
                <span>{auditData?.totalQuestions} Questions</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Users" size={14} />
                <span>{auditData?.completedUsers}/{auditData?.totalUsers} Completed</span>
              </div>
            </div>
          </div>

          <button
            onClick={handleExportReport}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            <Icon name="Download" size={20} />
            Export Report
          </button>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-card border border-border rounded-lg mb-6 overflow-x-auto">
        <div className="flex">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                activeTab === tab?.id
                  ? 'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && <ResponseOverview auditData={auditData} />}
        {activeTab === 'questions' && <QuestionWiseAnalysis auditData={auditData} />}
        {activeTab === 'timeline' && <TimelineTracking auditData={auditData} />}
        {activeTab === 'historical' && <HistoricalComparison auditData={auditData} />}
        {activeTab === 'sites' && <SiteComparison auditData={auditData} />}
        {activeTab === 'quality' && <ResponseQualityMetrics auditData={auditData} />}
        {activeTab === 'individuals' && <IndividualProgressTracker auditData={auditData} />}
      </div>
    </div>
  );
};

export default AuditResponseTracker;